import type { MaybeObservableValue, ObservableValue } from '@amazon/vinyl-observable'
import { asData, data, externalData } from '@amazon/vinyl-observable'
import type { LogTarget } from '@amazon/vinyl-util'
import { createDisposer, logError, logInfo } from '@amazon/vinyl-util'
import { formatMessage } from './format.js'
import type { MessageValue } from './format.js'
import { findBestLanguageMatch } from './locale.js'
import { getI18nConfig, getLocale, loadBundle, resolveLocale } from './store.js'

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

type ObservableParams<T> = {
    [K in keyof T]: MaybeObservableValue<T[K]>
}

/**
 * A localized string minted by the i18n layer. It is a nominal brand over
 * `string`: assignable *to* `string` anywhere (the brand widens away), but a
 * plain `string` is NOT assignable *to* `I18nString`. That one-way relationship
 * is what lets a sink require "this text came through localization" without
 * changing how the value behaves at runtime — the brand is erased at compile
 * time, so the runtime value is exactly the string it wraps.
 */
export type I18nString = string & { readonly __i18n: unique symbol }

/** The single sanctioned site for minting an {@link I18nString} from a string. */
export function asI18nString(s: string): I18nString {
    return s as I18nString
}

/**
 * A typed i18n bundle.
 *
 * - `K` enumerates the valid string keys.
 * - `P` maps each key to its required params object (plain values), or
 *   `undefined` if the key has no placeholders. At the call site, each
 *   param value is widened to `MaybeObservableValue` so callers can pass
 *   either static values or observables.
 */
export interface I18nBundle<
    K extends string,
    P extends Record<K, Record<string, string | number> | undefined> = Record<K, undefined>,
> {
    // `| ''` encodes the pre-load state: before the bundle JSON arrives (and for
    // a missing key), `.value` is empty. Feeding `.value` straight into a sink
    // that requires a non-empty I18nString is thus a compile error — use
    // getStringAsync (resolves post-load) or handle '' explicitly.
    getString<Key extends K>(
        this: void,
        key: Key,
        ...args: P[Key] extends undefined ? [] : [params: ObservableParams<P[Key]>]
    ): ObservableValue<I18nString | ''>

    /**
     * One-shot, locale-snapshot variant of {@link getString}. Same key/params,
     * but resolves a `Promise<I18nString>` once the bundle for the current
     * locale is loaded and the key is available (immediately if already cached)
     * — it does NOT re-emit when the locale later changes.
     */
    getStringAsync<Key extends K>(
        this: void,
        key: Key,
        ...args: P[Key] extends undefined ? [] : [params: ObservableParams<P[Key]>]
    ): Promise<I18nString>
}

// ---------------------------------------------------------------------------
// Implementation
// ---------------------------------------------------------------------------

const target: LogTarget = { logPrefix: 'i18n' }

/**
 * Creates a lazily-loaded i18n bundle bound to the given `bundleName`.
 *
 * On the first `.get()` call the bundle JSON for the current locale is
 * fetched. When the global locale changes, the new locale's JSON is fetched
 * and every observable returned by `.get()` is updated automatically.
 */
export function i18nBundle<
    K extends string,
    P extends Record<K, Record<string, string | number> | undefined> = Record<K, undefined>,
>(bundleName: string, availableLocales: readonly string[]): I18nBundle<K, P> {
    const strings$ = data<Record<string, MessageValue> | null>(null)
    // Source-locale (en-US) bundle, loaded lazily the first time a key misses
    // the active-locale bundle. `null` = not settled yet; an empty record marks
    // a settled-but-failed load so a genuine miss falls through to the raw key.
    const sourceStrings$ = data<Record<string, MessageValue> | null>(null)
    // Locale `strings` was loaded for — lets a miss tell "active locale IS the
    // source" (skip the redundant second load) from a real translation gap.
    let loadedBestLocale: string | undefined
    let sourceLoadStarted = false

    let initialized = false

    function loadForLocale(locale: string): void {
        const resolved = resolveLocale(locale)
        const bestLocale = findBestLanguageMatch(resolved, availableLocales) ?? resolved
        loadBundle(bundleName, bestLocale)
            .then((loaded) => {
                loadedBestLocale = bestLocale
                strings$.value = loaded
                logInfo(target, `Loaded bundle "${bundleName}" ${bestLocale}`)
            })
            // loadBundle already logged the failure; the current strings stay put.
            .catch(() => {})
    }

    function ensureInitialized() {
        if (initialized) return
        initialized = true
        getLocale().onData(loadForLocale)
    }

    // Fetch the source bundle once; feed it into `sourceStrings` so a subscribed
    // getString re-emits when it arrives. A failed load settles to an empty
    // record so a missing key still reaches the raw-key last resort.
    function ensureSourceLoaded() {
        if (sourceLoadStarted) return
        sourceLoadStarted = true
        loadBundle(bundleName, getI18nConfig().sourceLocale)
            .then((loaded) => (sourceStrings$.value = loaded))
            .catch(() => (sourceStrings$.value = {}))
    }

    function resolveValue(
        best: Record<string, MessageValue> | null,
        source: Record<string, MessageValue> | null,
        key: K
    ): MessageValue | null {
        if (!best) return null // active-locale bundle still loading → ''
        if (key in best) return best[key]

        // Missing from the active locale. When that locale IS the source there's
        // no second bundle to consult, so this is the true last resort.
        if (loadedBestLocale === getI18nConfig().sourceLocale) {
            logError(target, `Missing key "${key}"`)
            return key
        }

        // Fall back to the source locale so an untranslated key renders its
        // English value instead of the raw key id.
        ensureSourceLoaded()
        if (!source) return null // source still loading → '' (don't flash raw key)
        if (key in source) return source[key]

        logError(target, `Missing key "${key}"`)
        return key
    }

    function render(value: MessageValue, params: Record<string, string | number>, key: K): string {
        const resolvedLocale = resolveLocale(getLocale().value)
        try {
            return formatMessage(value, params, resolvedLocale)
        } catch (e) {
            logError(target, `Format error for key "${key}":`, e)
            return typeof value === 'string' ? value : key
        }
    }

    function getString<Key extends K>(
        key: Key,
        ...args: P[Key] extends undefined ? [] : [params: P[Key]]
    ): ObservableValue<I18nString | ''> {
        ensureInitialized()

        const params = args[0]
        const paramEntries = params ? Object.entries(params) : []
        const observables = paramEntries.map(([, v]) => asData(v))

        function calculateValue(): I18nString | '' {
            const value = resolveValue(strings$.value, sourceStrings$.value, key)
            if (value == null) return ''
            const paramObj: Record<string, string | number> = {}
            paramEntries.forEach(([k], i) => {
                paramObj[k] = observables[i].value
            })
            return asI18nString(render(value, paramObj, key))
        }

        // Lazy + leak-free, and subscribing to `strings`/`sourceStrings` directly
        // (each fires its live value on subscribe) resolves a late subscriber to
        // the loaded string instead of a stale pre-load '' — no keep-alive needed.
        return externalData<I18nString | ''>(calculateValue(), (setData) => {
            const { add, dispose } = createDisposer()
            const emit = () => setData(calculateValue())
            add(strings$.onData(emit))
            add(sourceStrings$.onData(emit))
            observables.forEach((obs) => add(obs.onData(emit)))
            return dispose
        })
    }

    async function getStringAsync<Key extends K>(
        key: Key,
        ...args: P[Key] extends undefined ? [] : [params: P[Key]]
    ): Promise<I18nString> {
        // Snapshot locale AND param values synchronously at call time — this is
        // a one-shot, so (unlike getString) neither a later setLocale nor a
        // later observable-param change affects the already-returned promise.
        const resolved = resolveLocale(getLocale().value)
        const bestLocale = findBestLanguageMatch(resolved, availableLocales) ?? resolved

        const params = args[0]
        const paramObj: Record<string, string | number> = {}
        if (params) {
            for (const [k, v] of Object.entries(params)) {
                paramObj[k] = asData(v).value
            }
        }

        // Same cached loader getString streams from — a hit resolves immediately.
        // A load failure is NOT swallowed: the rejection propagates so a resolved
        // value is always a real localized string. Unlike getString's reactive
        // observable (which re-emits once the bundle arrives), this one-shot has
        // no recovery, so consumers (e.g. toast) must never receive empty text.
        const loaded = await loadBundle(bundleName, bestLocale)

        // `loaded` is untrusted JSON typed as a total record, so a presence check
        // (not `== null`) is the honest guard.
        if (key in loaded) {
            return asI18nString(render(loaded[key], paramObj, key))
        }

        // Missing from the active locale: fall back to the source-locale value so
        // an untranslated key still resolves (unless the active locale already IS
        // the source, in which case there is nothing more to try).
        const sourceLocale = getI18nConfig().sourceLocale
        if (bestLocale !== sourceLocale) {
            const source = await loadBundle(bundleName, sourceLocale)
            if (key in source) {
                return asI18nString(render(source[key], paramObj, key))
            }
        }

        // Absent even in source: reject rather than resolve '' (or the raw key) —
        // either would surface as a blank or garbled toast.
        throw new Error(`i18n key "${key}" not found in bundle "${bundleName}"`)
    }

    return { getString, getStringAsync }
}
